/**
 * DetailsTab Component
 *
 * Displays detailed WHOIS information and origination/discovery details for seeds.
 * Shows domain registration data, IP information, and how the seed was discovered.
 *
 * @component
 */

import { Globe, RefreshCw } from 'lucide-react';

import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';
import { Entitled } from '@/components/Entitled';
import { NoData } from '@/components/legacy-ui/NoData';
import { Button } from '@/components/ui/button';
import { withRefreshBlockedReason } from '@/sections/seeds/components/drawer/tabs/RefreshBlockedReason';
import { useRefreshSeedData } from '@/sections/seeds/hooks/actions/useRefreshSeedData';
import {
  isSeedDomain,
  isSeedIP,
  Seed as SeedDiscriminated,
} from '@/sections/seeds/types';
import { isSeedRefreshable } from '@/sections/seeds/utils';
import { Seed } from '@/types';
import { cn } from '@/utils/classname';

interface DetailsTabProps {
  /** The seed to display details for */
  seed: Seed;
  /** Formatted details string (WHOIS data or IP information) */
  displayDetails?: string;
  /** Information about how the seed was discovered/originated */
  originationInfo: string[];
  /** Domain has no registration at its registry (definitive backend signal) */
  isDomainUnregistered?: boolean;
}

/**
 * Public-suffixes whose registries restrict public WHOIS/RDAP data. When a
 * domain ends in one of these, WHOIS details are often genuinely unavailable
 * (registry policy, not a collection failure), so the empty state says so.
 */
const RESTRICTED_WHOIS_SUFFIXES = ['.cn', '.ru', '.de'] as const;

const hasRestrictedWhoisSuffix = (identifier: string): boolean => {
  const lower = identifier.toLowerCase();
  return RESTRICTED_WHOIS_SUFFIXES.some(suffix => lower.endsWith(suffix));
};

/**
 * DetailsTab displays WHOIS/domain details and origination information
 *
 * For domains and IPs, shows detailed registration and infrastructure data.
 * Includes discovery source information (manual addition, integration discovery, etc.)
 *
 * @param seed - The seed object containing identifier and metadata
 * @param displayDetails - Formatted details content (JSON or plain text)
 * @param originationInfo - Array of discovery source descriptions
 */
export function DetailsTab({
  seed,
  displayDetails,
  originationInfo,
  isDomainUnregistered,
}: DetailsTabProps) {
  const { refresh, isRefreshing } = useRefreshSeedData();
  const discriminatedSeed = seed as unknown as SeedDiscriminated;
  const isDomain = isSeedDomain(discriminatedSeed);
  const isIP = isSeedIP(discriminatedSeed);
  // Status gate (F/FR/D) carries an explanatory tooltip; a refresh merely in
  // flight is disabled without one (the spinner already communicates it).
  const refreshStatusBlocked = !isSeedRefreshable(seed);
  const refreshDisabled = isRefreshing || refreshStatusBlocked;

  if (!displayDetails) {
    // The data-refresh empty state (Refresh button) only makes sense for seeds
    // the refresh can actually enrich: domains (whois capability) and IP/CIDR
    // (ip capability). For any other seed (e.g. web applications) the button
    // would be a visible no-op, so show a plain "no details" state instead.
    if (!isDomain && !isIP) {
      return (
        <div className="flex h-full flex-col">
          <NoData
            icon={
              <NoInformationIcon className="text-chariot-stroke mb-4 text-[100px]" />
            }
            title="No details collected yet"
          />
        </div>
      );
    }

    const onRefresh = () => refresh([discriminatedSeed]);

    // Definitive "not registered" is a domain-only signal — refresh can never
    // surface data unless the domain is registered later, so the CTA is demoted
    // to a ghost affordance. IP/CIDR seeds never reach this branch.
    if (isDomain && isDomainUnregistered) {
      return (
        <div className="flex h-full flex-col">
          <NoData
            icon={
              <Globe className="text-chariot-stroke mb-4 size-12 @xl:size-16 @2xl:size-20 @3xl:size-[100px]" />
            }
            title="This domain is not registered"
            description="Its registry reports no active registration, so there is no WHOIS data to collect. An unregistered seed is often a lapsed, mistyped, or dropped domain — consider removing it or confirming it's still yours. Refreshing will only find data if the domain is registered later."
            action={
              <Entitled entitlement="write_jobs" behavior="disable">
                {withRefreshBlockedReason(
                  <Button
                    onClick={onRefresh}
                    disabled={refreshDisabled}
                    size="sm"
                    variant="ghost"
                  >
                    <RefreshCw
                      className={cn(
                        'size-4',
                        isRefreshing && 'motion-safe:animate-spin'
                      )}
                    />
                    Refresh
                  </Button>,
                  refreshStatusBlocked
                )}
              </Entitled>
            }
          />
        </div>
      );
    }

    // Restricted-registry hint is a domain-only concern (registry WHOIS/RDAP
    // policy); IP/CIDR seeds share the blank state + Refresh button but not this
    // description.
    const restricted = isDomain && hasRestrictedWhoisSuffix(seed.identifier);
    return (
      <div className="flex h-full flex-col">
        <NoData
          icon={
            <NoInformationIcon className="text-chariot-stroke mb-4 text-[100px]" />
          }
          title="No WHOIS details collected yet"
          description={
            restricted
              ? 'Some registries (e.g. .cn) restrict public WHOIS/RDAP data, so details may be unavailable.'
              : undefined
          }
          action={
            <Entitled entitlement="write_jobs" behavior="disable">
              {withRefreshBlockedReason(
                <Button
                  onClick={onRefresh}
                  disabled={refreshDisabled}
                  size="sm"
                >
                  <RefreshCw
                    className={cn(
                      'size-4',
                      isRefreshing && 'motion-safe:animate-spin'
                    )}
                  />
                  Refresh
                </Button>,
                refreshStatusBlocked
              )}
            </Entitled>
          }
        />
      </div>
    );
  }

  return (
    <>
      <pre className="text-sm">{displayDetails}</pre>
      {originationInfo.length > 0 && (
        <div className="border-chariot-stroke mt-6 border-t pt-6">
          <div className="text-chariot-text-secondary flex flex-col gap-1 text-sm">
            {originationInfo.map((info, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="bg-chariot-stroke size-2 rounded-full" />
                {info}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
